"use client";

import { Brain, Heart, Moon, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function VentajasQueNosDistinguen() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    icon: Heart,
    title: "Capacitación Élite",
    description: "Nuestro personal atraviesa rigurosos procesos de formación táctica y ética, asegurando una respuesta disciplinada ante cualquier eventualidad crítica.",
    citation: "Certificación Federal de Seguridad, 2024"
  }, {
    icon: Moon,
    title: "Vigilancia 24/7",
    description: "Implementamos sistemas de monitoreo ininterrumpido que garantizan la protección de sus activos durante las horas de mayor vulnerabilidad operativa.",
    citation: "Protocolo de Reacción Inmediata Poseidón"
  }, {
    icon: Wind,
    title: "Tecnología de Punta",
    description: "Utilizamos rastreo GPS avanzado y geocercas inteligentes para un seguimiento vehicular preciso, optimizando la logística y la seguridad en tránsito.",
    citation: "Estándar Tecnológico de Seguridad Privada"
  }, {
    icon: Brain,
    title: "Atención Personalizada",
    description: "Diseñamos estrategias de seguridad a la medida de cada cliente, basándonos en análisis de riesgo detallados y diagnósticos de vulnerabilidad.",
    citation: "Consultoría Estratégica Grupo Poseidón"
  }];
  return <section data-section-id="991" id="ventajas-que-nos-distinguen" className="bg-background text-foreground relative overflow-hidden py-24 md:py-40 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-lg opacity-10 blur-3xl" style={{
      background: "radial-gradient(circle at 30% 30%, hsl(45 100% 52% / 0.4), transparent 70%)"
    }} />

      <div className="relative mx-auto max-w-6xl">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">
            Diferenciadores Estratégicos
          </p>
          <h2 className="font-serif font-default mt-6 text-4xl sm:text-5xl md:text-6xl font-semibold leading-[1.05] tracking-tight text-foreground">
            Ventajas que nos distinguen.
          </h2>
          <p className="mt-8 mx-auto max-w-[600px] text-base md:text-lg leading-relaxed text-muted-foreground font-default">
            En Grupo Poseidón, la seguridad no es un servicio, es una disciplina. Combinamos el rigor del personal altamente capacitado con la precisión de la tecnología más avanzada.
          </p>
        </div>

        {}
        <div className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <FeatureCard key={i} data-index={i} index={i} Icon={Icon} title={feature.title} description={feature.description} citation={feature.citation} />;
        })}
        </div>
      </div>
    </section>;
}
function FeatureCard({
  index,
  Icon,
  title,
  description,
  citation,
  ...rest
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
  citation: string;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} {...rest} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 120}ms`
  }}>
      <Card className="group h-full bg-card text-card-foreground rounded-md border border-border/40 p-8 md:p-12 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ease-out">
        <div className="flex flex-col gap-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-primary/10">
            <Icon className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>

          <h3 className="font-default text-2xl md:text-[28px] font-semibold leading-tight text-foreground">
            {title}
          </h3>

          <p className="text-sm md:text-[15px] leading-relaxed text-muted-foreground font-default">
            {description}
          </p>

          <div className="pt-4 mt-auto border-t border-border/40">
            <p className="font-mono text-xs md:text-sm text-primary/80">
              — {citation}
            </p>
          </div>
        </div>
      </Card>
    </div>;
}